/**
 * Consent Store
 *
 * Reactive state management for cookie consent using Zustand.
 * Syncs with vanilla-cookieconsent library state.
 *
 * @remarks
 * The source of truth is vanilla-cookieconsent (stored in cc_cookie).
 * This store mirrors that state for reactive components.
 */

import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

export const CONSENT_CATEGORIES = {
  NECESSARY: "necessary",
  ANALYTICS: "analytics",
  MARKETING: "marketing",
} as const;

export type ConsentCategory =
  (typeof CONSENT_CATEGORIES)[keyof typeof CONSENT_CATEGORIES];

interface ConsentState {
  // Consent categories
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;

  // Whether user has made a choice
  hasConsented: boolean;

  // Actions
  syncFromCookie: (categories: ConsentCategory[]) => void;
  acceptAll: () => void;
  rejectAll: () => void;

  // Helpers
  isAnalyticsAllowed: () => boolean;
  isMarketingAllowed: () => boolean;
}

export const consentStore = create<ConsentState>()(
  subscribeWithSelector((set, get) => ({
    necessary: true,
    analytics: false,
    marketing: false,
    hasConsented: false,

    syncFromCookie: (categories: ConsentCategory[]) => {
      set({
        necessary: true,
        analytics: categories.includes(CONSENT_CATEGORIES.ANALYTICS),
        marketing: categories.includes(CONSENT_CATEGORIES.MARKETING),
        hasConsented: true,
      });
    },

    acceptAll: () => {
      set({
        necessary: true,
        analytics: true,
        marketing: true,
        hasConsented: true,
      });
    },

    rejectAll: () => {
      set({
        necessary: true,
        analytics: false,
        marketing: false,
        hasConsented: true,
      });
    },

    isAnalyticsAllowed: () => get().analytics,
    isMarketingAllowed: () => get().marketing,
  }))
);

export const getConsentState = () => consentStore.getState();
export const subscribeToConsent = (listener: (state: ConsentState) => void) =>
  consentStore.subscribe(listener);
